import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const nextMonday = () => { const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); return d; };

/** "+ projection" — drop a headcount on a job before anyone is named; the dailies firm it up later. */
export const ProjectionDialog = ({ project, onClose, onCreated }) => {
  const mon = nextMonday();
  const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
  const [count, setCount] = useState(4);
  const [start, setStart] = useState(ymd(mon));
  const [end, setEnd] = useState(ymd(fri));
  const [time, setTime] = useState('07:00');
  const [weekends, setWeekends] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!count || count < 1) return toast.error('How many guys?');
    if (end < start) return toast.error('End is before start');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dailies/projections`, {
        project_id: project.project_number || project.id, count: Number(count), start_date: start, end_date: end,
        start_time: time, include_weekends: weekends, note,
      }, auth());
      const n = r.data.created?.length || 0;
      toast.success(`Projected ${count} guys on ${project.project_number}${n > 1 ? ` (${n} bars)` : ''}${r.data.skipped_days?.length ? ` · skipped ${r.data.skipped_days.length} day(s) already scheduled` : ''}`);
      onCreated?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the projection');
    }
    setBusy(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-4" onMouseDown={onClose} data-testid="projection-dialog">
      <form onSubmit={submit} onMouseDown={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-amber-600" />Projection</h3>
            <p className="text-xs text-slate-500 mt-0.5"><span className="font-semibold text-slate-700">{project.project_number}</span> · {project.title}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="projection-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <label className="block text-xs font-semibold text-slate-600">How many guys
              <input type="number" min={1} max={60} value={count} onChange={e => setCount(e.target.value)} autoFocus
                className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-lg font-bold text-slate-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none" data-testid="projection-count" />
            </label>
            <label className="block text-xs font-semibold text-slate-600 col-span-2">Start time
              <input type="time" value={time} onChange={e => setTime(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none" data-testid="projection-time" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-semibold text-slate-600">From
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none" data-testid="projection-start" />
            </label>
            <label className="block text-xs font-semibold text-slate-600">To
              <input type="date" value={end} min={start} onChange={e => setEnd(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm outline-none" data-testid="projection-end" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={weekends} onChange={e => setWeekends(e.target.checked)} className="w-4 h-4 accent-amber-600" data-testid="projection-weekends" />
            Include Saturday &amp; Sunday
          </label>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional) — e.g. waiting on steel, confirm with customer"
            className="w-full h-9 rounded-lg border border-slate-300 px-3 text-sm outline-none" data-testid="projection-note" />
          <p className="text-[11px] text-slate-500">Shows as an amber <span className="font-bold">{count || 'N'}?</span> bubble. Name the crew on the Dailies page and the bar firms up on its own; days already scheduled are skipped.</p>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={busy} className="h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="projection-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Add projection
          </button>
        </div>
      </form>
    </div>, document.body);
};

/** Inline editor shown in the work-period modal for projection bars. */
export const ProjectionEditor = ({ workPeriod, onSaved }) => {
  const [count, setCount] = useState(workPeriod.planned_crew || 1);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/dailies/projections/${workPeriod.id}`, { count: Number(count) }, auth());
      toast.success('Projection updated');
      onSaved?.(Number(count));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    setBusy(false);
  };
  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="wp-projection-editor">
      <div className="text-sm text-amber-900">
        <div className="font-bold">Projection — no names yet</div>
        <div className="text-xs">Name the crew on the <a href="/dailies" className="underline font-semibold">Dailies</a> page for these days and this bar firms up automatically.</div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <label className="text-xs font-semibold text-amber-900">Guys</label>
        <input type="number" min={1} max={60} value={count} onChange={e => setCount(e.target.value)} className="w-16 h-8 rounded-lg border border-amber-300 px-2 text-sm font-bold" data-testid="wp-projection-count" />
        <button type="button" onClick={save} disabled={busy || Number(count) === workPeriod.planned_crew} className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-bold disabled:opacity-50" data-testid="wp-projection-save">Save</button>
      </div>
    </div>
  );
};
